import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import useWindowDimentions from "../useWindowDimensions";

const perfectUrl = (url) => {
  return url.startsWith('http') ? url : "https://" + url;
};

const KominkaAkiyaGallery = memo(({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [validImages, setValidImages] = useState([]);
  
  const thumbnailsRef = useRef(null);
  const { width } = useWindowDimentions();

  // Enhanced image validation with loading states
  useEffect(() => {
    const validateImages = async () => {
      setIsLoading(true);
      
      if (!images || images.length === 0) {
        setValidImages([]);
        setIsLoading(false);
        return;
      }
      
      const checkImage = (url, index) =>
        new Promise((resolve) => {
          const img = new Image();
          const processedUrl = perfectUrl(url);
          img.src = processedUrl;
          img.onload = () => {
            setImageLoadStates(prev => ({ ...prev, [index]: 'loaded' }));
            resolve({ url: processedUrl, valid: true, index });
          };
          img.onerror = () => {
            setImageLoadStates(prev => ({ ...prev, [index]: 'error' }));
            resolve({ url: processedUrl, valid: false, index });
          };
        });

      const results = await Promise.all(images.map(checkImage));
      const filtered = results.filter((r) => r.valid).map((r) => r.url);
      setValidImages(filtered);
      setIsLoading(false);
    };

    validateImages();
  }, [images]);

  // Auto-scroll thumbnails to current image
  useEffect(() => {
    scrollToThumbnail(currentImageIndex);
  }, [currentImageIndex]);

  // Navigation functions
  const nextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % validImages.length);
  }, [validImages.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? validImages.length - 1 : prevIndex - 1
    );
  }, [validImages.length]);

  const changeImage = useCallback((index) => {
    setCurrentImageIndex(index);
  }, []);

  // Smooth scroll to thumbnail
  const scrollToThumbnail = useCallback((index) => {
    if (thumbnailsRef.current) {
      const thumbnailElement = thumbnailsRef.current.childNodes[index];
      if (thumbnailElement) {
        const container = thumbnailsRef.current;
        const top = thumbnailElement.offsetTop;
        const bottom = thumbnailElement.offsetTop + thumbnailElement.offsetHeight;
        const viewTop = container.scrollTop;
        const viewBottom = container.scrollTop + container.clientHeight;

        if (top < viewTop || bottom > viewBottom) {
          container.scrollTo({
            top: thumbnailElement.offsetTop - container.offsetTop,
            behavior: "smooth",
          });
        }
      }
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isFullscreen) {
        if (e.key === 'ArrowLeft') prevImage();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'Escape') setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFullscreen, prevImage, nextImage]);

  // Touch/swipe support
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && validImages.length > 1) nextImage();
    if (isRightSwipe && validImages.length > 1) prevImage();
  };

  if (isLoading) {
    return (
      <div className="mt-8 sm:h-[24rem] h-[20rem] md:h-[30rem] lg:h-[36rem] w-full flex items-center justify-center bg-orange-100 rounded-xl">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-[#e09321] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-orange-600">Loading traditional house gallery...</p>
        </div>
      </div>
    );
  }

  if (!validImages || validImages.length === 0) {
    return (
      <div className="mt-8 sm:h-[24rem] h-[20rem] md:h-[30rem] lg:h-[36rem] w-full bg-orange-100 rounded-xl flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 text-orange-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Images Available</h3>
          <p className="text-gray-500">Gallery images will appear here when available.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-8 sm:h-[24rem] h-[20rem] md:h-[30rem] lg:h-[36rem] w-full flex mb-12 gap-4">
        
        {/* Main Image Display */}
        <div className={`main-image relative h-full ${validImages.length > 1 && width >= 1024 ? 'lg:w-[80%]' : ''} w-full rounded-xl overflow-hidden shadow-lg group bg-orange-100`}>
          
          {/* Navigation Buttons - only show if more than 1 image */}
          {validImages.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-[#e09321]/80 backdrop-blur-md hover:bg-[#e09321] text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 z-10 focus:outline-none focus:ring-2 focus:ring-orange-300"
                aria-label="Previous image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-[#e09321]/80 backdrop-blur-md hover:bg-[#e09321] text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 z-10 focus:outline-none focus:ring-2 focus:ring-orange-300"
                aria-label="Next image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Main Image */}
          <img
            className={`h-full w-full object-cover transition-transform duration-300 ${validImages.length > 1 ? 'cursor-pointer hover:scale-105' : ''}`}
            src={validImages[currentImageIndex]}
            alt={`Traditional Japanese house view ${currentImageIndex + 1}`}
            onClick={validImages.length > 1 ? () => setIsFullscreen(true) : undefined}
            onTouchStart={validImages.length > 1 ? handleTouchStart : undefined}
            onTouchMove={validImages.length > 1 ? handleTouchMove : undefined}
            onTouchEnd={validImages.length > 1 ? handleTouchEnd : undefined}
          />

          {/* Image Counter - only show if more than 1 image */}
          {validImages.length > 1 && (
            <div className="absolute top-4 right-4 bg-[#e09321]/80 backdrop-blur-md text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {currentImageIndex + 1} / {validImages.length}
            </div>
          )}

          {/* Fullscreen Button - only show if more than 1 image */}
          {validImages.length > 1 && (
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute top-4 left-4 bg-[#e09321]/80 backdrop-blur-md hover:bg-[#e09321] text-white p-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-300"
              aria-label="View fullscreen"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}

          {/* Kominka Badge */}
          <div className="absolute bottom-4 left-4 bg-[#e09321]/90 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            🏮 Traditional Kominka
          </div>

          {/* Progress Indicator - only show if more than 1 image */}
          {validImages.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
              <div 
                className="h-full bg-[#e09321] transition-all duration-300 ease-out"
                style={{ width: `${((currentImageIndex + 1) / validImages.length) * 100}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* Thumbnail Gallery - Desktop Only and only if more than 1 image */}
        {width >= 1024 && validImages.length > 1 && (
          <div className="w-[20%] h-full">
            <div
              className="w-full flex flex-col h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-orange-300 scrollbar-track-orange-100 space-y-2 p-2"
              ref={thumbnailsRef}
            >
              {validImages.map((image, index) => (
                <button
                  key={index} 
                  className="relative cursor-pointer group flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#e09321] rounded-lg"
                  onClick={() => changeImage(index)}
                  type="button"
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className={`w-full aspect-square object-cover rounded-lg transition-all duration-300 ${
                      index === currentImageIndex 
                        ? "ring-3 ring-[#e09321] shadow-lg scale-105" 
                        : "opacity-70 hover:opacity-100 hover:scale-105"
                    }`}
                  />
                  
                  {/* Thumbnail overlay */}
                  {index !== currentImageIndex && (
                    <div className="absolute inset-0 bg-black/20 rounded-lg group-hover:bg-transparent transition-all duration-300"></div>
                  )}
                  
                  {/* Active indicator */}
                  {index === currentImageIndex && (
                    <div className="absolute top-2 right-2 w-3 h-3 bg-[#e09321] rounded-full shadow-md"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Thumbnail Strip - only if more than 1 image */}
      {width < 1024 && validImages.length > 1 && (
        <div className="flex overflow-x-auto space-x-3 pb-4 mb-8 scrollbar-thin scrollbar-thumb-orange-300 scrollbar-track-orange-100">
          {validImages.map((image, index) => (
            <button
              key={index} 
              className="relative cursor-pointer flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#e09321] rounded-lg"
              onClick={() => changeImage(index)}
              type="button"
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className={`w-20 h-20 object-cover rounded-lg transition-all duration-300 ${
                  index === currentImageIndex 
                    ? "ring-2 ring-[#e09321] shadow-md scale-105" 
                    : "opacity-60 hover:opacity-100 hover:scale-105"
                }`}
              />
              
              {/* Active indicator for mobile */}
              {index === currentImageIndex && (
                <div className="absolute top-1 right-1 w-2 h-2 bg-[#e09321] rounded-full shadow-sm"></div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Modal - only show if more than 1 image */}
      {isFullscreen && validImages.length > 1 && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center p-4">
            
            {/* Close Button */}
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-6 right-6 bg-[#e09321]/80 backdrop-blur-md hover:bg-[#e09321] text-white p-3 rounded-full transition-all duration-300 hover:scale-110 z-20"
              aria-label="Close fullscreen"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Fullscreen Navigation */}
            {validImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-[#e09321]/80 backdrop-blur-md hover:bg-[#e09321] text-white p-4 rounded-full transition-all duration-300 hover:scale-110 z-20"
                  aria-label="Previous image"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={nextImage}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-[#e09321]/80 backdrop-blur-md hover:bg-[#e09321] text-white p-4 rounded-full transition-all duration-300 hover:scale-110 z-20"
                  aria-label="Next image"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Fullscreen Image */}
            <img
              className="max-w-full max-h-full object-contain"
              src={validImages[currentImageIndex]}
              alt={`Traditional Japanese house fullscreen view ${currentImageIndex + 1}`}
            />

            {/* Fullscreen Counter */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-[#e09321]/80 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium">
              {currentImageIndex + 1} of {validImages.length}
            </div>

            {/* Fullscreen Kominka Badge */}
            <div className="absolute top-6 left-6 bg-[#e09321]/80 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold">
              🏮 Traditional Kominka Gallery
            </div>
          </div>
        </div>
      )}
    </>
  );
});

KominkaAkiyaGallery.displayName = 'KominkaAkiyaGallery';

export default KominkaAkiyaGallery;